const MAX_ASSET_COUNT = 5
const MAX_ASSET_BYTES = 20 * 1024 * 1024

const MIME_BY_EXTENSION = {
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  png: ['image/png'],
  webp: ['image/webp'],
  gif: ['image/gif'],
  txt: ['text/plain'],
  md: ['text/markdown', 'text/plain'],
  pdf: ['application/pdf'],
  doc: ['application/msword'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  xls: ['application/vnd.ms-excel'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  csv: ['text/csv', 'application/vnd.ms-excel', 'text/plain'],
}

function fileExtension(name) {
  const source = String(name || '')
  const index = source.lastIndexOf('.')
  return index >= 0 ? source.slice(index + 1).toLowerCase() : ''
}

function safeHttpUrl(value) {
  try {
    const parsed = new URL(String(value || ''))
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : ''
  } catch (_) {
    return ''
  }
}

export function validateAssetSelection(files, currentCount = 0) {
  const list = Array.from(files || [])
  if (currentCount + list.length > MAX_ASSET_COUNT) {
    return { ok: false, error: `每次最多上传 ${MAX_ASSET_COUNT} 个附件` }
  }
  for (const file of list) {
    const extension = fileExtension(file.name)
    const allowedMimes = MIME_BY_EXTENSION[extension]
    if (!allowedMimes) return { ok: false, error: `不支持的附件格式：${extension || '未知'}` }
    if (Number(file.size || 0) <= 0 || Number(file.size) > MAX_ASSET_BYTES) {
      return { ok: false, error: '单个附件不能超过 20MB，且不能为空文件' }
    }
    if (file.type && !allowedMimes.includes(String(file.type).toLowerCase())) {
      return { ok: false, error: `附件 MIME 类型与扩展名不匹配：${file.name}` }
    }
  }
  return { ok: true, error: '' }
}

export function assetIdsForRequest(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter(asset => asset?.assetId && asset.status === 'active')
    .map(asset => asset.assetId)
}

export function unsentAssetIds(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter(asset => asset?.assetId && asset.status === 'active' && !asset.sent)
    .map(asset => asset.assetId)
}

export function historyAttachments(message) {
  const contentParts = Array.isArray(message?.contentParts) ? message.contentParts : []
  const assetParts = contentParts.filter(part => part?.type === 'asset' && part.assetId)
  const fileUrls = Array.isArray(message?.fileUrls) ? message.fileUrls : []
  const fileById = new Map(fileUrls.filter(file => file?.assetId).map(file => [file.assetId, file]))
  const imageUrls = Array.isArray(message?.imgUrls) ? message.imgUrls : []
  let imageIndex = 0

  return assetParts.map(part => {
    const isImage = String(part.mediaType || '').startsWith('image/')
    const presentation = fileById.get(part.assetId) || {}
    const url = isImage ? imageUrls[imageIndex++] : presentation.url
    return {
      assetId: String(part.assetId),
      name: String(part.displayName || presentation.name || '附件'),
      mediaType: String(part.mediaType || presentation.mediaType || 'application/octet-stream'),
      size: Number(part.size ?? presentation.size ?? 0),
      status: String(presentation.status || part.status || 'active'),
      url: safeHttpUrl(url),
      sent: true,
      isImage,
    }
  })
}

export function normalizeUploadedAsset(data) {
  return {
    assetId: String(data?.assetId || ''),
    name: String(data?.displayName || '附件'),
    mediaType: String(data?.mediaType || 'application/octet-stream'),
    size: Number(data?.size || 0),
    status: String(data?.status || 'active'),
    url: safeHttpUrl(data?.url),
    sent: false,
    isImage: String(data?.mediaType || '').startsWith('image/'),
  }
}

export function formatAssetSize(size) {
  const bytes = Number(size || 0)
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
